import classNames from 'clsx';
import React, { type FC, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { useDuplicatesAnalytic } from 'reactApp/hooks/duplicates/useDuplicatesAnalytic';
import { useDuplicatesId } from 'reactApp/hooks/duplicates/useDuplicatesId';
import { loadDuplicatesListRequest, resetDuplicatesList } from 'reactApp/modules/duplicates/duplicates.actions';
import { DUPLICATES_PAGE_BASE_URL } from 'reactApp/modules/duplicates/duplicates.constants';
import {
    getDuplicatesDeletionComplete,
    getDuplicatesList,
    getDuplicatesListErrorState,
    getDuplicatesListIds,
    getDuplicatesListLoadedState,
} from 'reactApp/modules/duplicates/duplicates.selectors';
import { historyPush, routeChangeSuccess } from 'reactApp/modules/router/router.module';
import type { IHistoryPush } from 'reactApp/modules/router/router.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { DataList } from 'reactApp/ui/Datalist/DataList';
import { DuplicatesEmptyState } from 'reactApp/ui/Duplicates/DuplicatesEmptyState/DuplicatesEmptyState';
import { DuplicatesRemovedEmptyState } from 'reactApp/ui/Duplicates/DuplicatesRemovedEmptyState/DuplicatesRemovedEmptyState';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { filesPlural } from 'reactApp/utils/pluralHelpers';

import styles from './DuplicatesPage.css';

export const DuplicatesPage: FC = memo(function DuplicatesPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { initialItemId } = useDuplicatesId();

    const duplicates = useSelector(getDuplicatesListIds);
    const duplicatesList = useSelector(getDuplicatesList);
    const duplicatesLoaded = useSelector(getDuplicatesListLoadedState);
    const duplicatesError = useSelector(getDuplicatesListErrorState);
    const duplicatesCount = duplicates?.length;
    const isDeletionComplete = useSelector(getDuplicatesDeletionComplete);

    const analyticData = useDuplicatesAnalytic();

    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const isQuotaLoaded = useSelector(UserQuotaSelectors.isQuotaLoaded);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isB2BUser = useSelector(UserSelectors.isB2BUser);

    useEffect(() => {
        dispatch(
            routeChangeSuccess({ id: 'duplicates', storage: EStorageType.duplicates, params: {}, __isFolder: true, url: '', __parent: '' })
        );
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: 'duplicates' });
        dispatch(loadDuplicatesListRequest());
        return () => {
            dispatch(resetDuplicatesList());
        };
    }, []);

    useEffect(() => {
        if (duplicatesLoaded) {
            sendPaymentGa({
                eventCategory: ECategoryGa.entered,
                action: 'duplicates',
                ...analyticData,
                dwh_enabled: true,
                dwh_enabled_fields: [['groups_count']],
            });
        }
    }, [duplicatesLoaded]);

    useEffect(() => {
        if ((isOverquota && isQuotaLoaded) || isBizUser || isB2BUser) {
            navigate('/albums');
        }
    }, [isOverquota, isQuotaLoaded, isBizUser, isB2BUser, navigate]);

    useEffect(() => {
        if (initialItemId && !duplicatesList[initialItemId]) {
            const historyPushParams: IHistoryPush = { id: DUPLICATES_PAGE_BASE_URL };
            dispatch(historyPush(historyPushParams));
        }
    }, [dispatch, duplicatesList, initialItemId]);

    if (duplicatesLoaded && !duplicatesError && !duplicatesCount) {
        return (
            <div
                className={classNames(styles.root, {
                    [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
                })}
            >
                <div className={styles.wrapper}>
                    <div className={classNames(styles.header)}>
                        <BreadcrumbsContainer />
                    </div>

                    {isDeletionComplete ? <DuplicatesRemovedEmptyState /> : <DuplicatesEmptyState />}

                    <Footer storage={EStorageType.duplicates} isAbsolutePos={true} />
                </div>
            </div>
        );
    }

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            <div className={styles.wrapper}>
                <div className={classNames(styles.header)}>
                    <BreadcrumbsContainer />
                    {duplicatesCount > 0 && (
                        <div className={styles.filesCountWrapper}>
                            <div className={styles.filesCount} data-qa-id="duplicates-counter">
                                {duplicatesCount} {filesPlural(duplicatesCount)}
                            </div>
                        </div>
                    )}
                </div>
                <DataList goTo={noop} initialItemId={initialItemId} selectBoxEnabled={false} />
            </div>
        </div>
    );
});
